import { useState, useRef, useCallback, useEffect, type KeyboardEvent } from 'react';
import type { MentionInputElement } from './MentionTextField';
import type { ChatTab, SavedAction } from '@/types';
import { normalizeMentionText } from '@/utils/atMention';
import { getSlashContext, insertSlashCommand } from '@/utils/slashCommand';
import { getChatInputPlaceholder } from '@/config/chatInput';
import { ChevronDownIcon, SendArrowIcon } from '@/components/icons';
import { SystemContextLabel } from '@/components/SystemContextLabel';
import type { ChatInputVariant } from '@/types/chatInput';
import { cn } from '@/utils/helpers';
import { MentionTextField } from './MentionTextField';
import { SlashCommandDropdown } from './SlashCommandDropdown';
import { ComposerAttachMenu } from './ComposerAttachMenu';
import { CsvAttachmentPreview } from './CsvAttachmentPreview';
import type { ComposerSendOptions } from '@/types/composer';
import { WORKSPACE_FLOORS } from '@/config/static/workspace';

const TABS: { id: ChatTab; label: string; icon: React.ReactNode }[] = [
  {
    id: 'users',
    label: 'Users',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M2 12c0-2.5 2.2-4 5-4s5 1.5 5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'devices',
    label: 'Devices',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <rect x="1" y="2" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M5 12h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'credentials',
    label: 'Credentials',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M4 7h2M8 7h2M4 9h1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'events',
    label: 'Events',
    icon: (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <rect x="1" y="2" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
        <path d="M4 1v2M10 1v2M1 6h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    ),
  },
];

interface ChatInputProps {
  userSite: string;
  /** Category tabs (Users, Devices, …) — enabled on workspace home only. */
  showTabs?: boolean;
  onSend: (message: string, options?: ComposerSendOptions) => void;
  /** `workspace` = home tabs + tall field; `flow` = guided flows (Figma 31-2962). */
  variant?: ChatInputVariant;
  savedActions?: SavedAction[];
  initialValue?: string;
  draftInjectKey?: number;
  draftText?: string;
  placeholder?: string;
  className?: string;
  /** Blocks send only; typing and / @ commands stay enabled. */
  disabled?: boolean;
  /** Controlled composer tab — sync with QuickActionCards when provided. */
  activeTab?: ChatTab;
  defaultActiveTab?: ChatTab;
  onActiveTabChange?: (tab: ChatTab) => void;
}

export function ChatInput({
  userSite,
  showTabs = false,
  onSend,
  variant = 'workspace',
  savedActions = [],
  initialValue = '',
  draftInjectKey = 0,
  draftText = '',
  placeholder,
  className,
  disabled = false,
  activeTab: activeTabProp,
  defaultActiveTab = 'users',
  onActiveTabChange,
}: ChatInputProps) {
  const [internalTab, setInternalTab] = useState<ChatTab>(defaultActiveTab);
  const isTabControlled = activeTabProp !== undefined;
  const activeTab = isTabControlled ? activeTabProp : internalTab;

  const setActiveTab = useCallback(
    (tab: ChatTab) => {
      if (!isTabControlled) setInternalTab(tab);
      onActiveTabChange?.(tab);
    },
    [isTabControlled, onActiveTabChange]
  );
  const [value, setValue] = useState(initialValue);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [showFloorMenu, setShowFloorMenu] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState<string | null>(null);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const textareaRef = useRef<MentionInputElement>(null);
  const floorMenuRef = useRef<HTMLDivElement>(null);

  const syncTriggers = useCallback((val: string, cursorPos: number) => {
    const slashCtx = getSlashContext(val, cursorPos);

    if (slashCtx) {
      setShowSlashMenu(true);
      setSlashQuery(slashCtx.query);
      return;
    }

    setShowSlashMenu(false);
    setSlashQuery('');
  }, []);

  useEffect(() => {
    if (draftInjectKey < 1) return;
    setValue(draftText);
    setShowSlashMenu(false);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [draftInjectKey, draftText]);

  useEffect(() => {
    if (!showFloorMenu) return;
    const handler = (e: MouseEvent) => {
      if (floorMenuRef.current && !floorMenuRef.current.contains(e.target as Node)) {
        setShowFloorMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFloorMenu]);

  const mentionPickerActive = mentionMenuOpen;

  const canSend = Boolean(value.trim() || attachedFile);

  const dispatchSend = useCallback(() => {
    if (disabled || !canSend) return;
    const message = normalizeMentionText(value.trim());
    onSend(message, { attachment: attachedFile });
    setValue('');
    setShowSlashMenu(false);
    setAttachedFile(null);
    setAttachError(null);
  }, [value, onSend, disabled, canSend, attachedFile]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<MentionInputElement>) => {
      if (e.key === 'Escape' && showSlashMenu) {
        e.preventDefault();
        setShowSlashMenu(false);
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey && !showSlashMenu && !mentionPickerActive) {
        e.preventDefault();
        dispatchSend();
      }
    },
    [dispatchSend, showSlashMenu, mentionPickerActive]
  );

  const handleMentionFieldChange = useCallback(
    (val: string, cursorPos: number) => {
      setValue(val);
      syncTriggers(val, cursorPos);
    },
    [syncTriggers]
  );

  const handleSlashSelect = useCallback((template: string) => {
    const el = textareaRef.current;
    const cursorPos = el?.selectionStart ?? value.length;
    const { value: next, cursorPos: nextPos } = insertSlashCommand(value, cursorPos, template);
    setValue(next);
    setShowSlashMenu(false);
    setSlashQuery('');
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(nextPos, nextPos);
    });
  }, [value]);

  const handleSend = useCallback(() => {
    dispatchSend();
  }, [dispatchSend]);

  const showAttachMenu = (variant === 'workspace' && showTabs) || variant === 'flow';

  const resolvedPlaceholder =
    placeholder ?? getChatInputPlaceholder(variant, showTabs);

  return (
    <div className={cn('chat-input', `chat-input--${variant}`, className)}>
      <div className="chat-input__box">
        {showTabs && (
          <div className="chat-input__tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`chat-input__tab${activeTab === tab.id ? ' chat-input__tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        )}

        <div className="chat-input__textarea-wrap">
          <MentionTextField
            ref={textareaRef}
            className="mention-field--in-chat"
            variant="composer"
            mentionMode="entity"
            singleLine={false}
            value={value}
            onChange={handleMentionFieldChange}
            onKeyDown={handleKeyDown}
            placeholder={resolvedPlaceholder}
            suppressMentionPicker={showSlashMenu}
            onMentionPickerChange={setMentionMenuOpen}
            rows={variant === 'flow' ? 2 : 2}
            aria-label="Message"
          />
          {showSlashMenu && (
            <SlashCommandDropdown
              actions={savedActions}
              query={slashQuery}
              onSelect={handleSlashSelect}
              onClose={() => setShowSlashMenu(false)}
            />
          )}
        </div>

        {attachError ? (
          <p className="chat-input__attach-error" role="alert">
            {attachError}
          </p>
        ) : null}

        <div className={`chat-input__footer${showAttachMenu ? ' chat-input__footer--with-attach' : ''}`}>
          {showAttachMenu ? (
            <div className="chat-input__footer-left">
              <ComposerAttachMenu
                onFileSelect={(file) => {
                  setAttachedFile(file);
                  setAttachError(null);
                }}
                onError={setAttachError}
              />
              {attachedFile ? (
                <CsvAttachmentPreview
                  file={attachedFile}
                  onRemove={() => setAttachedFile(null)}
                />
              ) : null}
            </div>
          ) : (
            <span className="chat-input__footer-spacer" aria-hidden />
          )}

          <div className="chat-input__footer-actions">
          <div className="chat-input__floor-wrap" ref={floorMenuRef}>
            <button
              type="button"
              className="chat-input__site-selector"
              onClick={() => setShowFloorMenu((s) => !s)}
              aria-haspopup="listbox"
              aria-expanded={showFloorMenu}
            >
              <SystemContextLabel
                label={selectedFloor ?? userSite}
                className="chat-input__site-context"
                iconVariant="plain"
                iconSize={20}
              />
              <ChevronDownIcon
                size={11}
                className={`chat-input__site-chevron${showFloorMenu ? ' chat-input__site-chevron--open' : ''}`}
              />
            </button>
            {showFloorMenu && (
              <ul className="chat-input__floor-menu" role="listbox">
                <li>
                  <button
                    type="button"
                    className={`chat-input__floor-option${!selectedFloor ? ' chat-input__floor-option--active' : ''}`}
                    role="option"
                    aria-selected={!selectedFloor}
                    onClick={() => {
                      setSelectedFloor(null);
                      setShowFloorMenu(false);
                    }}
                  >
                    All floors
                  </button>
                </li>
                {WORKSPACE_FLOORS.map((floor) => (
                  <li key={floor}>
                    <button
                      type="button"
                      className={`chat-input__floor-option${selectedFloor === floor ? ' chat-input__floor-option--active' : ''}`}
                      role="option"
                      aria-selected={selectedFloor === floor}
                      onClick={() => {
                        setSelectedFloor(floor);
                        setShowFloorMenu(false);
                      }}
                    >
                      {floor}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button
            type="button"
            className="chat-input__send"
            onClick={handleSend}
            disabled={disabled || !canSend}
            aria-label="Send message"
          >
            <SendArrowIcon size={40} />
          </button>
          </div>
        </div>
      </div>

      <p className="chat-input__disclaimer">
        AI proposes, you approve. No auto-actions on devices, users, or credentials.
      </p>
    </div>
  );
}
